import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { StyleSheet, View, } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSequence, withTiming, interpolate, } from 'react-native-reanimated';
import useColorScheme from 'hooks/useColorScheme';
import { Button } from 'design-system/Button';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import Squircle from 'design-system/Squircle';
import EmojiText from 'design-system/EmojiText';
import useStyles from 'hooks/useStyles';
import FastImage from 'utils/packages/rnFastImage';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { isValidAppLink } from 'hooks/useAppLink';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import { FeedLinkStyles } from '../types';
import { isWeb } from '../../../constants';
const NotificationItem = ({ item, style, onPress, showDot = false, onLayout, }) => {
    const { heading, text, coloredIconUrl, iconUrl, link, clickable, emoji } = item;
    const styles = useStyles(styleSet);
    const { hasClickable, hasButton, hasHyperlink } = useMemo(() => {
        const hasClickable = !isWeb && clickable && ('url' in clickable || 'appLink' in clickable);
        const hasButton = !hasClickable &&
            !isWeb &&
            link &&
            link.linkStyle === FeedLinkStyles.BUTTON;
        const hasHyperlink = !hasClickable &&
            !isWeb &&
            link &&
            link.linkStyle === FeedLinkStyles.HYPERLINK;
        return { hasClickable, hasButton, hasHyperlink };
    }, [clickable, link]);
    const rotationSharedValue = useSharedValue(0);
    const wiggleAnimatedStyle = useAnimatedStyle(() => ({
        transform: [
            {
                rotate: `${interpolate(rotationSharedValue.value, [0, 0.25, 0.5, 0.75, 1], [0, 5, 0, -5, 0])}deg`,
            },
        ],
    }), [rotationSharedValue]);
    const pressed = useCallback(() => {
        if (hasClickable && clickable) {
            if ('appLink' in clickable &&
                clickable.appLink &&
                isValidAppLink(clickable.appLink)) {
                onPress(clickable.appLink);
                return;
            }
            if ('url' in clickable && clickable.url) {
                Links.openLink(clickable.url);
                return;
            }
        }
        if ((hasHyperlink || hasButton) && link) {
            if ('url' in link && link.url) {
                Links.openLink(link.url);
                return;
            }
            if ('appLink' in link && link.appLink && isValidAppLink(link.appLink)) {
                onPress(link.appLink);
                return;
            }
        }
        rotationSharedValue.value = withSequence(withTiming(1, { duration: 500 }), withTiming(0, { duration: 0 }));
    }, [
        clickable,
        hasButton,
        hasClickable,
        hasHyperlink,
        link,
        onPress,
        rotationSharedValue,
    ]);
    const isDarkMode = useColorScheme() === 'dark';
    const colors = useColors();
    const iconFallback = isDarkMode
        ? 'https://storage.googleapis.com/emma-app.com/images/icons/notifications/dark/emma_team.png'
        : 'https://storage.googleapis.com/emma-app.com/images/icons/notifications/light/emma_team.png';
    const icon = isDarkMode ? iconUrl : coloredIconUrl;
    const containerRef = useRef(null);
    const handleLayout = useCallback(() => {
        if (onLayout && containerRef.current) {
            setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.measureInWindow(onLayout);
                }
            }, 200);
        }
    }, [onLayout]);
    const [key, setKey] = useState();
    useEffect(() => {
        // This is to force onLayout to run again when the notification screen space header is rendered
        if (onLayout) {
            setKey(Math.random().toString());
        }
    }, [onLayout]);
    const hasUrlOrAppLink = link && ('appLink' in link || 'url' in link) && link.linkText;
    return (<View ref={containerRef} key={key} onLayout={onLayout ? handleLayout : undefined}>
      <Animated.View style={wiggleAnimatedStyle}>
        <TouchableBounce style={[styles.row, style]} onPress={pressed}>
          <View style={styles.iconContainer}>
            <View style={styles.imageWrapper}>
              {emoji ? (<Squircle width={rem(40)} height={rem(40)} color={colors.background.dark}>
                  <EmojiText fontSize={rem(20)}>{emoji}</EmojiText>
                </Squircle>) : (<FastImage source={{
                uri: icon || iconFallback,
            }} style={staticStyles.image} resizeMode="contain"/>)}
              {showDot ? <View style={styles.notificationDot}/> : null}
            </View>
          </View>
          <View style={styles.description}>
            {heading ? (<Text Text-16 style={styles.title}>
                {heading}
              </Text>) : null}
            <Text TextThin-14 Secondary>
              {text}
            </Text>
            {!!hasButton && hasUrlOrAppLink ? (<View style={styles.btnWrapper}>
                <Button square brandReversed title={link.linkText} onPress={pressed} containerStyle={styles.btn}/>
              </View>) : null}
            {!!hasHyperlink && hasUrlOrAppLink ? (<Text Text-14 Primary style={styles.hyperlink}>
                {link.linkText}
              </Text>) : null}
          </View>
        </TouchableBounce>
      </Animated.View>
    </View>);
};
const staticStyles = StyleSheet.create({
    image: {
        width: rem(40),
        height: rem(40),
    },
});
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
        padding: rem(16),
        borderRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    iconContainer: {
        width: rem(40),
        alignItems: 'center',
    },
    imageWrapper: {
        position: 'relative',
        width: rem(40),
        height: rem(40),
    },
    description: {
        flex: 1,
        marginLeft: rem(16),
        flexDirection: 'column',
    },
    title: {
        marginBottom: rem(4),
    },
    btn: {
        marginTop: rem(12),
        paddingHorizontal: rem(24),
    },
    btnWrapper: {
        alignSelf: 'flex-start',
    },
    hyperlink: {
        marginTop: rem(4),
    },
    notificationDot: {
        position: 'absolute',
        top: rem(-3),
        right: rem(-3),
        width: rem(7),
        height: rem(7),
        borderRadius: rem(7),
        backgroundColor: colors.elements.statusNegative,
    },
}));
export default NotificationItem;
